import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import _ from 'underscore';

import { CanvasContainer, ChartTotalSpan, ChartZeroResultSpan, BoldSpan, Note } from './styles';
import { categories } from 'util/appConstants';

export const DistributionChart = ({
  test,
  isFreeCode,
  showTotalTestsTaken = true,
  showCompanyPopulationDistribution,
  ticket,
}) => {
  let totalTestsTaken = 0;
  let data = [];
  let tooltips = {};
  let plugins = [];
  let target;
  let targetIndex;
  let labelsRef = useRef([]);
  let percent40Index = useRef(0);
  let percent70Index = useRef(0);

  var distribution = showCompanyPopulationDistribution ? test.companyTestDistribution : test.distribution;

  (() => {
    if (percent40Index.current) percent40Index.current = 0;
    if (percent70Index.current) percent70Index.current = 0;
  })();

  if (ticket?._id) {
    var differenceInterval = test.differenceInterval;
    if (test.adaptiveTest) {
      targetIndex = _.indexOf(
        _.map(test.distribution, function (d) {
          return d.maxValue;
        }),
        parseInt(ticket.rating / differenceInterval) * differenceInterval
      );
      target = targetIndex;
    } else if (test.category === categories.CODE.category) {
      const MAX_VALUE_FOR_FIRST_BAR = 4;
      targetIndex = _.indexOf(
        _.map(test.distribution, function (d, index) {
          return d.maxValue ? d.maxValue : 0;
        }),
        ticket.result > 0 && ticket.result <= MAX_VALUE_FOR_FIRST_BAR
          ? MAX_VALUE_FOR_FIRST_BAR
          : parseInt(ticket.result / differenceInterval) * differenceInterval
      );
    } else {
      targetIndex = (ticket.result || 0) - 1;
    }
  }
  if (distribution) {
    if (test.adaptiveTest) {
      data = _.pluck(distribution, 'count');
      totalTestsTaken = distribution.reduce((tot, dist) => tot + dist.count, 0);
      let testsTakenCounter = 0;
      data = _.reject(data, function (d, index) {
        if (d === 0) {
          if (index < target) {
            // Shift target index (the bar to be selected) if necessary
            targetIndex -= 1;
          }
          return true;
        }
        return false;
      });
      labelsRef.current = data.map((d) => {
        testsTakenCounter += d;
        // Round to one decimal and convert to percentage
        return Math.round(((100 * testsTakenCounter) / totalTestsTaken) * 10) / 10 + '%';
      });
      tooltips = {
        callbacks: {
          title: function (tooltipItems) {
            return `Better than or equal to ${tooltipItems[0].label} of results`;
          },
          label: function (context) {
            const value = context.dataset.data[context.dataIndex];
            return value === 1 ? '1 test taker belongs to this group' : `${value} test takers belong to this group`;
          },
        },
      };
    } else {
      data = _.map(distribution, (dist) => {
        var barValue = _.isObject(dist) ? dist.count : dist;
        totalTestsTaken += barValue;
        return barValue;
      });
      labelsRef.current = _.map(distribution, (dist, index) => {
        return dist.label ? dist.label : isFreeCode ? index : index + 1;
      });
      tooltips = {
        callbacks: {
          title: function (context) {
            return `Result: ${context[0].label}`;
          },
          label: function (context) {
            const value = context.dataset.data[context.dataIndex];
            return value === 1 ? '1 test taker got this result' : `${value} test takers got this result`;
          },
        },
      };
    }
  }

  if (totalTestsTaken < 15 && !showCompanyPopulationDistribution) {
    return (
      <CanvasContainer>
        <Note>
          We are currently collecting data about this test and you will soon be able to see a test distribution.
        </Note>
      </CanvasContainer>
    );
  }

  if (!isFreeCode) {
    let distributionWithoutZero = _.reject(distribution, function (d) {
      if (d.count === 0) {
        return true;
      }
      return false;
    });

    let distributionToUse = test.adaptiveTest ? distributionWithoutZero : distribution;
    _.reduce(
      distributionToUse,
      function (count, bar, index) {
        if (bar && typeof bar === 'object') {
          bar = bar.count;
        }
        if (percent40Index.current === 0 && count + bar > totalTestsTaken * 0.4) {
          percent40Index.current = index;
        }

        if (percent70Index.current === 0 && count + bar > totalTestsTaken * 0.7) {
          percent70Index.current = index;
        }

        return count + bar;
      },
      0
    );

    plugins.push({
      id: 'annotationFor40And70Percentages',
      afterDatasetsDraw: ({ ctx, scales }) => {
        var higherIndexLabel, lowerIndexLabel, index;

        // 40% index bar
        if (test.adaptiveTest) {
          var label40Index = parseFloat(
            labelsRef.current[percent40Index.current].substring(0, labelsRef.current[percent40Index.current].length - 1)
          );
          if (label40Index > 40) {
            higherIndexLabel = label40Index;
            lowerIndexLabel = parseFloat(
              labelsRef.current[percent40Index.current - 1].substring(
                0,
                labelsRef.current[percent40Index.current - 1].length - 1
              )
            );
            index = percent40Index.current - 1 + (40 - lowerIndexLabel) / (higherIndexLabel - lowerIndexLabel);
          } else {
            lowerIndexLabel = label40Index;
            higherIndexLabel = parseFloat(
              labelsRef.current[percent40Index.current + 1].substring(
                0,
                labelsRef.current[percent40Index.current + 1].length - 1
              )
            );
            index = percent40Index.current + +((40 - lowerIndexLabel) / (higherIndexLabel - lowerIndexLabel));
          }
        } else {
          index = labelsRef.current[percent40Index.current];
        }

        if (index) {
          let xaxis = scales['x'];
          let yaxis = scales['y'];

          ctx.save();
          ctx.font = '15px';
          ctx.textBaseline = 'bottom';
          ctx.fillStyle = '#000000';
          ctx.fillText('40%', xaxis.getPixelForValue(index) - 30, 20);
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(xaxis.getPixelForValue(index), yaxis.top);
          ctx.strokeStyle = '#C4AE1F';
          ctx.lineTo(xaxis.getPixelForValue(index), yaxis.bottom);
          ctx.stroke();
        }

        // 70% index bar
        if (test.adaptiveTest) {
          var label70Index = parseFloat(
            labelsRef.current[percent70Index.current].substring(0, labelsRef.current[percent70Index.current].length - 1)
          );
          if (label70Index > 70) {
            higherIndexLabel = label70Index;
            lowerIndexLabel = parseFloat(
              labelsRef.current[percent70Index.current - 1].substring(
                0,
                labelsRef.current[percent70Index.current - 1].length - 1
              )
            );
            index = percent70Index.current - 1 + (1 / (higherIndexLabel - lowerIndexLabel)) * (70 - lowerIndexLabel);
          } else {
            lowerIndexLabel = label70Index;
            higherIndexLabel = parseFloat(
              labelsRef.current[percent70Index.current + 1].substring(
                0,
                labelsRef.current[percent70Index.current + 1].length - 1
              )
            );
            index = percent70Index.current + (1 / (higherIndexLabel - lowerIndexLabel)) * (70 - lowerIndexLabel);
          }
        } else {
          index = labelsRef.current[percent70Index.current];
        }

        if (index) {
          let xaxis = scales['x'];
          let yaxis = scales['y'];

          ctx.save();
          ctx.font = '15px';
          ctx.textBaseline = 'bottom';
          ctx.fillStyle = '#000000';
          ctx.fillText('70%', xaxis.getPixelForValue(index) + 10, 20);
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(xaxis.getPixelForValue(index), yaxis.top);
          ctx.strokeStyle = '#8EBC3C';
          ctx.lineTo(xaxis.getPixelForValue(index), yaxis.bottom);
          ctx.stroke();
        }
      },
    });
  }

  let colors = Array(data.length).fill('#e5e5e5');
  if (targetIndex != null && targetIndex >= 0 && targetIndex < data.length) {
    for (let i = 0; i < targetIndex; i += 1) {
      colors[i] = 'rgba(204, 233, 195, 1)';
    }
    colors[targetIndex] = 'rgba(126, 198, 102, 1)';
  }

  const chartData = {
    labels: labelsRef.current,
    datasets: [
      {
        data: data,
        borderWidth: 1,
        backgroundColor: colors,
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: tooltips,
    },
    events: ['mousemove'],
    maintainAspectRatio: true,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Results',
        },
        ticks: {
          autoSkip: false,
          // The 90 & 0 are the default values
          maxRotation: test.categoryName === isFreeCode ? 45 : 90,
          minRotation: test.categoryName === isFreeCode ? 45 : 0,
        },
        grid: {
          display: false,
        },
        stacked: true,
      },

      y: {
        title: {
          scaleStyle: 'bold',
          display: true,
          text: 'Tests Taken',
        },
        grid: {
          display: false,
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <CanvasContainer>
      {showTotalTestsTaken && (
        <>
          <ChartTotalSpan>
            Total tests taken:
            <BoldSpan>
              {totalTestsTaken +
                (showCompanyPopulationDistribution ? test.zeroResultCountForCompany ?? 0 : test.zeroResultCount ?? 0)}
            </BoldSpan>
          </ChartTotalSpan>
          {test.zeroResultCount > 0 && (
            <ChartZeroResultSpan>
              out of which {showCompanyPopulationDistribution ? test.zeroResultCountForCompany : test.zeroResultCount}{' '}
              got 0 points
            </ChartZeroResultSpan>
          )}
        </>
      )}
      <Bar data={chartData} options={options} plugins={plugins}></Bar>
    </CanvasContainer>
  );
};
